if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      reviewsLink = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      sectionUpdateControllers = new Map();
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        document.addEventListener('DOMContentLoaded', this.checkRatingsLink.bind(this));
      }

      checkRatingsLink() {
        const containerId = 'klaviyo-reviews-all';
        const reviewsContainer = document.getElementById(containerId);
        if (reviewsContainer) {
          if (window.location.href.includes(`#${containerId}`)) {
            this.smoothScrollTo( reviewsContainer );
          }

          this.reviewsLink = this.querySelector(`a[href="#${containerId}"]`);
          this.reviewsLink.addEventListener('click', (event) => {
            event.preventDefault();
              this.smoothScrollTo( reviewsContainer );
          }); 
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();
        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;

        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        if (target.dataset.fallbackOption2) {
          selectedOptionValues[1] = target.dataset.fallbackOption2;
        }

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, this.sectionId, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedOptionValues),
        });
      }

      resetProductFormState() {
        this.productForms.forEach(form => {
          form.toggleSubmitButton(true);
          form.handleErrorMessage();
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              true
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
              true
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSectionUpdate({requestUrl, callback, key}) {
        // One controller per target section, keyed by its `data-section` name so the accordion
        // fetch never cancels the value-propositions one. Without this, a quick run of variant
        // changes leaves two responses racing for the same node and both insertBefore into it —
        // the section ends up in the DOM twice, the loser carrying the wrong variant's colours.
        this.sectionUpdateControllers.get(key)?.abort();
        const controller = new AbortController();
        this.sectionUpdateControllers.set(key, controller);

        fetch(requestUrl, { signal: controller.signal })
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      handleSectionUpdate(currentSection) {
        return (html) => {
          // A section can legitimately render nothing for the selected variant (an empty
          // render gate, e.g. value-propositions without `mn_benefits.list`). The fetched
          // markup then carries no `[data-section]`, and handing null to viewTransition
          // would throw on `newContent.outerHTML` — caught below, but the section then keeps
          // the previous variant's content instead of updating.
          const newSection = html.querySelector('[data-section]');
          if (!newSection) return;

          // The node captured when the request went out can already be gone — viewTransition
          // reaches for `oldNode.parentNode.insertBefore`, which throws on a detached node.
          if (!currentSection.isConnected) return;

          HTMLUpdateUtility.viewTransition(currentSection, newSection);
        }
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getSelectedVariantImage(productInfoNode) {
        const getSelectedVariantImage = productInfoNode.querySelector('variant-selects [data-has-selected-image]')?.innerHTML;
        return !!getSelectedVariantImage ? JSON.parse(getSelectedVariantImage) : null;
      }

      buildRequestUrlWithParams(url, optionValues, sectionId, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${sectionId}`);

        const urlObj = new URL(window.location.href);
        const favParam = urlObj.searchParams.get('fav');
        if (favParam) {
          params.push(`fav=${favParam}`);
        }

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        let variantSelects = html.querySelector('variant-selects');
        let variantSelectsFavorites = variantSelects.querySelector('.variant_options.favorites');
        let currentPageInfo = document.querySelector('product-info');
        let currentVariantSelects = currentPageInfo.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [], true);
        }
      }

      handleUpdateProductInfo(productUrl, selectedOptionValues) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const hasVariantImage = this.getSelectedVariantImage(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };
          updateSourceFromDestination('buy-box-buttons');

          // Re-initialize Loop widget after buy-box-buttons HTML replacement
          const loopProductForm = this.querySelector('product-form[data-loop-product-id]');
          if (loopProductForm) {
            const loopProductId = loopProductForm.dataset.loopProductId;
            checkLoopWidget(parseInt(loopProductId));
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.querySelector('.tiered-pricing-wrapper')?.classList.remove('hidden');
          this.querySelector(`#Quantity-Form-${this.dataset.section}`)?.classList.remove('hidden');


          this.updateMedia(html, variant?.featured_media?.id);

          updateSourceFromDestination('sticky-cta');
          updateSourceFromDestination('tracking');
          updateSourceFromDestination('price');
          updateSourceFromDestination('badges');
          updateSourceFromDestination('tiered-pricing');
          updateSourceFromDestination('stress-bar');
          updateSourceFromDestination('product-quote');  
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('subtitle');
          updateSourceFromDestination('delivery')
          updateSourceFromDestination('payment-icons');
          updateSourceFromDestination('Quantity-Form');
          updateSourceFromDestination('product-accordion');
          updateSourceFromDestination('promotional-code');

          if (hasVariantImage) {
            updateSourceFromDestination('image-gallery');
          }

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#sticky-button-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#sticky-text-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
          this.productForms.forEach(form => {
            form?.toggleSubmitButton(
              form.querySelector(`button[type="submit"]`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          });

          const valueProportionsSection = document.querySelector('[data-section="value-propositions"]');
          const accordionSection = document.querySelector('[data-section="product-accordion"]');
          const productComparisonSection = document.querySelector('[data-section="product-comparison"]');

          if (accordionSection) {
            this.getSectionUpdate({
              requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, accordionSection.dataset.sectionId),
              callback: this.handleSectionUpdate(accordionSection),
              key: 'product-accordion',
            });
          }

          // MNR-769: the section's background colour follows the variant, not just its
          // packshot, so it has to re-render for variants without a featured media too.
          if (valueProportionsSection) {
            this.getSectionUpdate({
              requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, valueProportionsSection.dataset.sectionId),
              callback: this.handleSectionUpdate(valueProportionsSection),
              key: 'value-propositions',
            });
          }

          if (productComparisonSection && variant?.featured_media?.id) {
            this.getSectionUpdate({
              requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, productComparisonSection.dataset.sectionId),
              callback: this.handleSectionUpdate(productComparisonSection),
              key: 'product-comparison',
            });
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="items[0][id]"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        const urlObj = new URL(window.location.href);
        const favParam = urlObj.searchParams.get('fav');
        let newUrl = `${url}${favParam ? `?fav=${favParam}` : ''}${variantId ? (favParam ? `&variant=${variantId}` : `?variant=${variantId}`) : ''}`;
        window.history.replaceState({}, '', newUrl);
      }

      setUnavailable() {
        this.productForms.forEach(form => form?.toggleSubmitButton(true, window.variantStrings.unavailable));
        document.querySelector(`#buy-buttons-${this.dataset.section}`).classList.remove('hidden');

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Price-Per-Portion', 'Volume-Note', 'Volume', 'Quantity-Rules', 'Quantity-Form', 'delivery', 'payment-icons','multi-add', 'badges', 'stress-bar','sticky-button','sticky-text', 'back-in-stock']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
        this.querySelector('.tiered-pricing-wrapper')?.classList.add('hidden');
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        let currentVariantId = null;
        this.productForms.forEach(form => {
          if (!currentVariantId) currentVariantId = form.variantIdInput?.value;
        });
        if (!currentVariantId) return;
        if (this.querySelector('.quantity__rules-cart .loading__spinner')) {
          this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
          return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              this.updateQuantityRules(this.dataset.section, html);
            })
            .catch((e) => console.error(e))
            .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
        }
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        if (quantityFormUpdated) {
          for (let selector of selectors) {
            const current = this.quantityForm.querySelector(selector);
            const updated = quantityFormUpdated.querySelector(selector);
            if (!current || !updated) continue;
            if (selector === '.quantity__input') {
              const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
              for (let attribute of attributes) {
                const valueUpdated = updated.getAttribute(attribute);
                if (valueUpdated !== null) {
                  current.setAttribute(attribute, valueUpdated);
                } else {
                  current.removeAttribute(attribute);
                }
              }
            } else {
              current.innerHTML = updated.innerHTML;
            }
          }
        }
      }

      smoothScrollTo(el, {
        duration = 1000,
        maxFollowMs = 2000,
        settleFrames = 30,
        easing = t => t < 0.5 ? 4*t*t*t : 1 - Math.pow(-2*t+2,3) / 2
        } = {}
      ) {
        if (!el) return;

        const startTime = performance.now();
        let startY = window.scrollY;
        let stableCount = 0;
        let lastTargetY = null;

        function currentTargetY() {
          return window.scrollY + el.getBoundingClientRect().top - 120;
        }

        function step(now) {
          const elapsed = now - startTime;
          const t = Math.min(elapsed / duration, 1);

          const targetY = currentTargetY();

          if (lastTargetY !== null && Math.abs(targetY - lastTargetY) < 1) {
            stableCount++;
          } else {
            stableCount = 0;
          }
          lastTargetY = targetY;

          const eased = easing(t);
          const nextY = Math.round(startY + (targetY - startY) * eased);
          window.scrollTo(0, nextY);

          const reachedTimeLimit = elapsed >= Math.max(duration, maxFollowMs);
          const settled = stableCount >= settleFrames;

          if (!reachedTimeLimit && (!settled || t < 1)) {
            requestAnimationFrame(step);
          } else {
            window.scrollTo(0, Math.round(currentTargetY()));
          }
        }

        requestAnimationFrame(step);
      }

      get productForms() {
        return this.querySelectorAll(
          `product-form#ProductForm-${this.sectionId},
           product-form#ProductForm-${this.sectionId}-cta`
        );
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
